import {
  CatalogItem,
  ServiceChargeCatalog,
  useCatalogItems,
  useInvoiceDiscount,
  useInvoices,
  useServiceChargeCatalogs,
  useTaxRate,
} from '@melio/ar-domain';
import { uniq } from 'lodash';

import { InvoiceFormValuesLineItem } from '../types';
import { useGetPayloadFromFormData } from './line-item-utils';
import { InvoiceFormValues } from './useInvoiceForm';

type CreateInvoiceActionsProps = {
  discountId?: string;
  taxRateId?: string;
  serviceChargeIds?: string[];
  onError?: ErrorFunction;
};

export const useCreateInvoiceActions = ({
  discountId,
  taxRateId,
  serviceChargeIds = [],
  onError,
}: CreateInvoiceActionsProps) => {
  const { updateCatalogItems, isSaving: isSavingCatalogItems } = useUpdateCatalogItems();
  const { create, isMutating: isCreatingInvoice } = useInvoices({ enabled: false });
  const { data: catalogItems = [] } = useCatalogItems({ onError });
  const { data: discount } = useInvoiceDiscount({ id: discountId, onError });
  const { data: taxRate } = useTaxRate({ id: taxRateId, onError });
  const { data: serviceChargesCatalog } = useServiceChargeCatalogs({
    enabled: serviceChargeIds.length > 0,
    params: { ids: uniq(serviceChargeIds) },
    onError,
  });

  const getPayloadFromFormData = useGetPayloadFromFormData();

  // There can be multiple service charges with the same id, so we need to map them to the actual entities
  // (the API returns one entity instance per unique id)
  const relevantServiceCharges = serviceChargeIds
    .map((id) => serviceChargesCatalog?.find((serviceChargeCatalogItem) => serviceChargeCatalogItem.id === id))
    .filter(Boolean) as ServiceChargeCatalog[];

  const getPayload = (data: InvoiceFormValues) =>
    getPayloadFromFormData(data, catalogItems, discount, taxRate, relevantServiceCharges);

  async function createInvoice(formData: InvoiceFormValues) {
    await updateCatalogItems(formData.lineItems);

    const createdInvoice = await create(getPayload(formData));

    return createdInvoice;
  }

  return {
    createInvoice,
    isSaving: isCreatingInvoice || isSavingCatalogItems,
  };
};

const useUpdateCatalogItems = () => {
  const { data = [], update, isMutating } = useCatalogItems();

  const updateCatalogItems = (lineItems: InvoiceFormValuesLineItem[]) =>
    Promise.all(
      lineItems
        .map((lineItem) => {
          const catalogItem = data.find((item) => item.id === lineItem.catalogItemId);
          if (!catalogItem) {
            throw new Error('Catalog item not found');
          }
          if (catalogItem.price === lineItem.price && catalogItem.taxable === lineItem.taxable) {
            return null;
          }
          return update(catalogItem.id, { price: lineItem.price, taxable: lineItem.taxable });
        })
        .filter(Boolean) as Promise<CatalogItem>[]
    );

  return {
    updateCatalogItems,
    isSaving: isMutating,
  };
};

// exporting for tests only
export const __useUpdateCatalogItems = useUpdateCatalogItems;
